import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavParams, RefresherCustomEvent } from '@ionic/angular';
import { filter, map, ReplaySubject, Subscription } from 'rxjs';
import { PushManagerProvider } from 'src/providers/push/push-manager.provider';
import { PushProvider } from 'src/providers/push/push.provider';
import { ChatProvider } from 'src/providers/chat.provider';
import { EnquiryApplicationStatus, EnquiryApplicationStatusDictionary, EnquiryProvider, EnquiryStatusDictionary } from 'src/providers/enquiry.provider';
import { HapticsProvider, HapticType } from 'src/providers/haptics.provider';
import { TabsProvider } from 'src/providers/tabs/tabs.provider';
import { TranslatorProvider } from 'src/providers/translator.provider';
import { ChatItemStyle, ChatItemType } from 'src/shared/models/chat/chat-item.model';
import { ChatType } from 'src/shared/models/chat/chat.model';
import { EnquiryUserApplicationModel } from 'src/shared/models/enquiries/enquiry-user-application.model';
import { EnquiryStatus, EnquiryType } from 'src/shared/models/enquiries/enquiry.model';
import { NotificationType } from 'src/shared/models/push-notification/push-notification';

@Component({
    selector: 'rota-page-enquiries',
    styleUrl: 'enquiries.page.scss',
    templateUrl: 'enquiries.page.html',
    standalone: false
})
export class EnquiriesPage implements OnInit, OnDestroy {
  private userApplicationGroupsCache$ = new ReplaySubject<any>(1);

  pushSub: Subscription;
  selectedDateFrom: Date;
  selectedDate: Date | null = null;
  selectedDateTo: Date | null;
  displayDates: {
    date: Date,
    isActive: boolean
  }[] = [];

  additionalDisplayMonths = [
    { monthCount: 1, isActive: false },
    { monthCount: 2, isActive: false }
  ];

  isChatOpen = false;

  isLoading: boolean;

  get locale() {
    return this.translator.currentLanguage;
  }

  constructor(
    public tabsProvider: TabsProvider,
    public chatService: ChatProvider,
    public translator: TranslatorProvider,
    public params: NavParams,
    public enquiryService: EnquiryProvider,
    public pushProvider: PushProvider,
    public pushManagerProvider: PushManagerProvider,
    private hapticsProvider: HapticsProvider,
  ) { }

  async ngOnInit(): Promise<void> {
    this.subscribeToNewMessage();
    this.subscribeToSort();

    this.displayCalendarDates();

    await this.enquiryService.sortApplicationsByDate(this.selectedDateFrom);
    await this.readExpiredApplications();
    await this.fetchApplications();
  }

  async readExpiredApplications() {
    const isReadExpired = await this.enquiryService.readExpiredApplications();
    if (isReadExpired) await this.pushManagerProvider.fetchNotifications();
  }

  async fetchApplications() {
    this.isLoading = true;

    try {
      const fetchDateFrom = new Date();
      fetchDateFrom.setHours(0, 0, 0, 0);
      const fetchDateTo = new Date();
      fetchDateTo.setMonth(fetchDateTo.getMonth() + this.additionalDisplayMonths[this.additionalDisplayMonths.length - 1].monthCount);
      fetchDateTo.setHours(0, 0, 0, 0);

      await this.enquiryService.fetchApplications(fetchDateFrom, fetchDateTo);

      await this.enquiryService.sortApplicationsByDate(this.selectedDateFrom);

      if (this.selectedDate) this.selectDays(this.selectedDate);
    }
    finally {
      this.isLoading = false;
    }
  }

  displayCalendarDates() {
    const paramDate = this.params.get('model');
    const today = paramDate ? new Date(paramDate) : new Date();
    today.setHours(0, 0, 0, 0);

    this.selectedDateFrom = today;

    const startDay = new Date();
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date();
    endDay.setDate(endDay.getDate() + 6);
    endDay.setHours(0, 0, 0, 0);

    const dateArray = this.getDates(startDay, endDay);
    dateArray.forEach(date => {
      this.displayDates.push({
        date: date,
        isActive: this.selectedDateFrom.getDate() === date.getDate()
      });
    });
  }

  subscribeToNewMessage() {
    this.pushSub = this.pushProvider.onPushNotification$
      .pipe(filter(notification => notification.type === NotificationType.EnquiryMessage))
      .subscribe(async notify => {
        const notifyMeta = JSON.parse(notify.additional);

        const application = this.enquiryService.findApplication(notify.associatedEntryId);
        if (!application) return;

        this.chatService.appendNewMessage({
          title: notifyMeta.EnquiryStatus === EnquiryStatus.None
            ? ''
            : notifyMeta.EnquiryStatus === EnquiryStatus.PartialApproval
              ? this.translator.dictionary.Partial_approve
              : notifyMeta.EnquiryStatus === EnquiryStatus.Confirmed
                ? application.application.title + ' ' + this.translator.dictionary.was_approved
                : application.application.title + ' ' + this.translator.dictionary.was_rejected,
          username: notify.createdBy,
          message: notifyMeta.EnquiryMessage,
          fromClient: notifyMeta.FromClient,
          createdAt: notify.createdAt,
          icon: EnquiryStatusDictionary[notifyMeta.EnquiryStatus as EnquiryStatus],
          itemType: notifyMeta.EnquiryType === EnquiryType.Message ? ChatItemType.Message : ChatItemType.Notice,
          style: notifyMeta.EnquiryStatus === EnquiryStatus.Confirmed
            ? ChatItemStyle.Success
            : notifyMeta.EnquiryStatus === EnquiryStatus.Rejected
              ? ChatItemStyle.Danger
              : ChatItemStyle.Warning,
        });

        if (this.isChatOpen) {
          await this.enquiryService.read(notifyMeta.UserEnquiryApplicationId);
          await this.pushManagerProvider.fetchNotifications();
        }
        else {
          application.isRead = false;
          await this.fetchApplications();
        }
      });
  }

  subscribeToSort() {
    this.enquiryService.sortedApplications$.pipe(
      map((apps: any) => {
        return apps.reduce((accum: any, userApp: any) => {
          const statusGroup = accum.find((s: any) => s.status === EnquiryApplicationStatus[userApp.status]);
          if (statusGroup) {
            statusGroup.userApplications.push(userApp);
          } else {
            accum.push({
              status: EnquiryApplicationStatus[userApp.status],
              icon: EnquiryApplicationStatusDictionary[userApp.status as EnquiryStatus],
              userApplications: [userApp]
            });
          }
          return accum;
        }, new Array<{ status: string, icon: string, userApplications: EnquiryUserApplicationModel[] }>());
      })
    ).subscribe(this.userApplicationGroupsCache$);
  }

  private getDates(startDate: Date, stopDate: Date): Array<Date> {
    const dateArray = new Array();
    const currentDate = startDate;
    while (currentDate <= stopDate) {
      dateArray.push(new Date(currentDate));
      currentDate.setHours(24);
    }

    return dateArray;
  }

  get userApplicationGroups() {
    return this.userApplicationGroupsCache$.asObservable();
  }

  get showLoadButton() {
    return this.enquiryService.total > this.enquiryService.count;
  }

  async openChat(application: EnquiryUserApplicationModel) {
    if (!application.isRead) {
      this.enquiryService.read(application.id)
        .then(() => this.pushManagerProvider.fetchNotifications());
    }

    this.isChatOpen = true;

    await this.chatService.open(EnquiryUserApplicationModel.toChatModel(application, this.translator), ChatType.Enquiries, async () => {
      this.isChatOpen = false;

      const fetchDateFrom = new Date();
      fetchDateFrom.setHours(0, 0, 0, 0);
      const fetchDateTo = new Date();
      fetchDateTo.setMonth(fetchDateTo.getMonth() + this.additionalDisplayMonths[this.additionalDisplayMonths.length - 1].monthCount);
      fetchDateTo.setHours(0, 0, 0, 0);

      this.enquiryService.read(application.id)
        .then(() => this.pushManagerProvider.fetchNotifications());

      await this.enquiryService.sortApplicationsByDate(this.selectedDateFrom, this.selectedDateTo);
    });
  }

  async loadMoreApplications() {
    const page = this.enquiryService.filter.page + 1;
    await this.enquiryService.uploadApplications(page);
  }

  getType(status: EnquiryApplicationStatus): string {
    return EnquiryApplicationStatus[status].toLowerCase();
  }

  async selectDays(date: Date) {
    this.hapticsProvider.vibrate(HapticType.Click);
    this.selectedDate = date;
    this.selectedDateFrom = date;
    this.selectedDateTo = null;
    this.displayDates.forEach(d => d.isActive = date.getDate() === d.date.getDate());
    this.additionalDisplayMonths.forEach(day => day.isActive = false);
    this.enquiryService.sortApplicationsByDate(this.selectedDateFrom);
  }

  selectMonths(index: number) {
    this.hapticsProvider.vibrate(HapticType.Click);

    const currDate = new Date();
    currDate.setHours(0, 0, 0, 0);
    const toDate = new Date();
    toDate.setMonth(toDate.getMonth() + this.additionalDisplayMonths[index].monthCount);
    toDate.setHours(0, 0, 0, 0);

    this.selectedDateFrom = currDate;
    this.selectedDateTo = toDate;
    this.displayDates.forEach(date => date.isActive = false);
    this.additionalDisplayMonths.forEach((day, i) => day.isActive = i === index);

    this.enquiryService.sortApplicationsByDate(currDate, toDate);
  }

  async handleRefresh(event: RefresherCustomEvent) {
    await this.fetchApplications();
    this.hapticsProvider.vibrate(HapticType.Click);
    event.target.complete();
  }

  async goBack() {
    await this.tabsProvider.forwardBack();
  }

  ngOnDestroy(): void {
    if (this.pushSub) this.pushSub.unsubscribe();
    this.isChatOpen = false;
    this.selectedDate = null;
  }
}
